import React, { createContext, useContext, useState, useEffect } from 'react'
import toast from 'react-hot-toast'

const AuthContext = createContext(null)

const AUTH_STORAGE_KEY = 'ch24_auth_session'

// Helper for safe JSON fetching with detailed error extraction
const safeAuthFetch = async (url, options = {}) => {
  try {
    const res = await fetch(url, options)
    const text = await res.text()
    
    let data
    try {
      data = JSON.parse(text)
    } catch {
      // If server returned HTML or plain error (e.g. 500 PHP error / Apache rewrite)
      console.error(`Non-JSON response from ${url}:`, text)
      const errorSnippet = text.replace(/<[^>]*>?/gm, '').trim().slice(0, 150)
      return {
        success: false,
        error: errorSnippet || `Server error (HTTP ${res.status}: ${res.statusText})`
      }
    }
    return data
  } catch (netErr) {
    console.error(`Network error connecting to ${url}:`, netErr)
    return {
      success: false,
      error: `Network error: ${netErr.message || 'Unable to reach backend API'}`
    }
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const saved = localStorage.getItem(AUTH_STORAGE_KEY)
      return saved ? JSON.parse(saved) : null
    } catch {
      return null
    }
  })

  const [isLoading, setIsLoading] = useState(false)

  // Sync to localStorage
  useEffect(() => {
    if (user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user))
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY)
    }
  }, [user])

  // 1. Send OTP (Email Verification via SMTP)
  const sendOtp = async (email, purpose = 'LOGIN') => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'send_otp', email, purpose })
      })

      if (data.success) {
        toast.success(data.message || `Verification code sent to ${email}`)
        return { success: true, data }
      } else {
        toast.error(data.error || 'Failed to send verification code')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 2. Verify OTP
  const verifyOtp = async (email, otp, purpose = 'LOGIN') => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verify_otp', email, otp, purpose })
      })

      if (data.success) {
        toast.success('Code Verified!')
        return { success: true, data }
      } else {
        toast.error(data.error || 'Invalid verification code.')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 3. Login with OTP
  const loginWithOtp = async ({ email, otp }) => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login_with_otp', email, otp })
      })

      if (data.success && data.user) {
        const sessionUser = {
          ...data.user,
          token: data.token,
          role: 'resident'
        }
        setUser(sessionUser)
        toast.success(`Welcome, ${sessionUser.fullName || 'Resident'}!`)
        return { success: true, user: sessionUser }
      } else {
        toast.error(data.error || 'Login failed.')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 4. Sign Up New Account
  const signup = async ({ fullName, email, phone, password, preferredSociety }) => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signup', fullName, email, phone, password, preferredSociety })
      })

      if (data.success && data.user) {
        const sessionUser = {
          ...data.user,
          token: data.token,
          role: 'resident'
        }
        setUser(sessionUser)
        toast.success(data.message || 'Account created successfully!')
        return { success: true, user: sessionUser }
      } else {
        toast.error(data.error || 'Sign up failed.')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 5. Reset Password
  const resetPassword = async ({ email, otp, newPassword }) => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reset_password', email, otp, newPassword })
      })

      if (data.success) {
        toast.success(data.message || 'Password reset successfully!')
        return { success: true }
      } else {
        toast.error(data.error || 'Password reset failed.')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 6. Resident Password Login
  const loginResident = async ({ identifier, password }) => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'resident_login', identifier, password })
      })

      if (data.success && data.user) {
        const sessionUser = {
          ...data.user,
          token: data.token,
          role: 'resident'
        }
        setUser(sessionUser)
        toast.success(`Welcome back, ${sessionUser.fullName || 'Resident'}!`)
        return { success: true, user: sessionUser }
      } else {
        toast.error(data.error || 'Invalid resident credentials.')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 7. Admin Login
  const loginAdmin = async ({ username, password }) => {
    setIsLoading(true)
    const normalizedUser = (username || '').trim()
    const rawPass = (password || '').trim()

    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'admin_login', username: normalizedUser, password: rawPass })
      })

      if (data.success && data.user) {
        const sessionUser = {
          ...data.user,
          token: data.token,
          role: 'admin'
        }
        setUser(sessionUser)
        toast.success(`Welcome back, ${sessionUser.username || 'Admin'}!`)
        return { success: true, user: sessionUser }
      } else {
        toast.error(data.error || 'Invalid admin credentials. Please check your username and password.')
        return { success: false, error: data.error || 'Invalid credentials' }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 8. Track Applicant KYC Status
  const trackApplication = async (identifier) => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'track_status', identifier })
      })
      return data
    } finally {
      setIsLoading(false)
    }
  }

  // 9. Login with Google OAuth
  const loginWithGoogle = async (credential) => {
    setIsLoading(true)
    try {
      const data = await safeAuthFetch('/api/auth.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'google_login', credential })
      })

      if (data.success && data.user) {
        const sessionUser = {
          ...data.user,
          token: data.token,
          role: data.user.role || 'resident'
        }
        setUser(sessionUser)
        toast.success(`Welcome, ${sessionUser.fullName || sessionUser.name}!`)
        return { success: true, user: sessionUser }
      } else {
        toast.error(data.error || 'Google sign-in failed.')
        return { success: false, error: data.error }
      }
    } finally {
      setIsLoading(false)
    }
  }

  // 10. Logout
  const logout = () => {
    setUser(null)
    localStorage.removeItem(AUTH_STORAGE_KEY)
  }

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin' || user?.role === 'staff' || user?.role === 'manager',
    isResident: user?.role === 'resident',
    sendOtp,
    verifyOtp,
    loginWithOtp,
    loginResident,
    loginAdmin,
    loginWithGoogle,
    signup,
    resetPassword,
    trackApplication,
    logout
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
